import { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';

import { INSTANCE_STATE } from '../../../apis/constants';
import Table from '../../elements/table/Table';
import Text from '../../elements/text/Text';
import Modal from '../../sections/modal/Modal';
import ModalButtons from '../../sections/modal/ModalButtons';

const convertToRawCidrs = (cidrList) => {
  if (cidrList.length === 0) {
    return [];
  } else {
    return cidrList.map((c) => c.cidr);
  }
};

const convertToFormattedCidrs = (cidrList) => {
  if (cidrList.length === 0) {
    return [];
  } else {
    return cidrList.map((c) => ({ cidr: c }));
  }
};

const CidrAddressSection = ({ cidrs, instanceState, onUpdateCidrRange }) => {
  const { t } = useTranslation();

  const [removeIpRange, setRemoveIpRange] = useState(null);
  const rawCidrList = convertToRawCidrs(cidrs);

  const openRemoveModal = (selectedIp) => setRemoveIpRange(selectedIp);
  const closeRemoveModal = () => setRemoveIpRange(null);

  const handleRemove = () => {
    const updatedCidrList = rawCidrList.filter(
      (cidr) => cidr !== removeIpRange
    );
    const formattedCidrs = convertToFormattedCidrs(updatedCidrList);
    onUpdateCidrRange(formattedCidrs);
    closeRemoveModal();
  };

  const tableHeaders = {
    ipRange: {
      name: t('ipRange', { ns: 'fields' }),
      sortable: true,
    },
    actions: {
      name: '',
      sortable: false,
    },
  };

  const tableRows = cidrs.map((c) => ({
    ipRange: c.cidr,
    actions: (
      <Button
        key={c.cidr}
        aria-label="delete"
        color="error"
        size="small"
        onClick={() => openRemoveModal(c.cidr)}
        disabled={instanceState !== INSTANCE_STATE.RUNNING}
        startIcon={<DeleteIcon />}
      >
        {t('delete', { ns: 'buttons' })}
      </Button>
    ),
  }));

  return (
    <section>
      <Box>
        <Text>
          <Trans
            t={t}
            i18nKey="settings.cidrSection"
            ns="descriptions"
            components={[
              <Link
                key="0"
                target="_blank"
                href="https://www.keycdn.com/support/what-is-cidr"
                rel="noreferrer"
                underline="hover"
              />,
            ]}
          />
        </Text>
        <Table
          small
          headers={tableHeaders}
          rows={tableRows}
          emptyText={t('noIpSet', { ns: 'descriptions' })}
        />
        <Modal
          action={
            <ModalButtons
              onAction={handleRemove}
              onCancel={closeRemoveModal}
              cancelProps={{ type: 'reset' }}
              actionText={t('delete', { ns: 'buttons' })}
              danger
            />
          }
          openModal={!!removeIpRange}
          title={t('deletion', {
            ns: 'project',
            text: t('ipRange', { ns: 'fields' }),
          })}
          handleClose={closeRemoveModal}
        >
          <Text>
            {t('deleteIpRange', {
              ns: 'descriptions',
              text: removeIpRange,
            })}
          </Text>
        </Modal>
      </Box>
    </section>
  );
};

CidrAddressSection.propTypes = {
  cidrs: PropTypes.array.isRequired,
  instanceState: PropTypes.string,
  onUpdateCidrRange: PropTypes.func.isRequired,
};

export default CidrAddressSection;
